import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "./AuthContext";

// Local dev only: the backend serves these routes only in Development with Auth:DevLogin:Enabled; elsewhere
// this renders nothing. See eng-docs "AD - Dev Login & Manual-Test Personas".

type Persona = {
  key: string;
  label: string;
  email: string;
  organisationName: string | null;
  role: string;
  scope: "Organisation" | "Client";
};

export function DevLoginPanel({ onError }: { onError: (message: string | null) => void }) {
  const { devLogin } = useAuth();
  const [personas, setPersonas] = useState<Persona[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/proxy/v1/auth/dev-login/personas")
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data: Persona[] | null) => {
        if (!cancelled && Array.isArray(data)) setPersonas(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!personas || personas.length === 0) return null;

  const groups = new Map<string, Persona[]>();
  for (const persona of personas) {
    const group = persona.scope === "Client" ? `${persona.organisationName} (client)` : persona.organisationName ?? "";
    groups.set(group, [...(groups.get(group) ?? []), persona]);
  }

  const signIn = async (persona: Persona) => {
    setBusy(persona.key);
    onError(null);
    try {
      const result = await devLogin({ key: persona.key, label: persona.label });
      if (result.error) onError(result.error);
    } catch {
      onError("Couldn't reach the backend for dev login. Is it running?");
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    setBusy("reset");
    try {
      const resp = await fetch("/api/proxy/v1/auth/dev-login/reset", { method: "POST" });
      if (!resp.ok) {
        const problem = await resp.json().catch(() => null);
        toast.error(problem?.detail ?? "Couldn't reset the manual-test data.");
        return;
      }
      toast.success("Manual-test organisations reset to their seed data.");
    } catch {
      toast.error("Couldn't reset the manual-test data.");
    } finally {
      setBusy(null);
      setConfirmReset(false);
    }
  };

  return (
    <div className="mt-2 w-full border-t pt-4 text-left" data-testid="dev-login-panel">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Dev login · local only</p>
      <div className="mt-3 flex flex-col gap-4">
        {[...groups.entries()].map(([group, members]) => (
          <div key={group}>
            <p className="mb-1.5 text-sm font-medium">{group}</p>
            <div className="flex flex-wrap gap-1.5">
              {members.map((persona) => (
                <Button
                  key={persona.key}
                  size="sm"
                  variant="secondary"
                  title={persona.email}
                  disabled={busy !== null}
                  onClick={() => signIn(persona)}
                  data-testid={`dev-login-${persona.key}`}
                >
                  {busy === persona.key ? "Signing in…" : persona.role}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2">
        {confirmReset ? (
          <>
            <span className="text-xs text-muted-foreground">Delete and reseed both test orgs?</span>
            <Button size="sm" variant="destructive" disabled={busy !== null} onClick={reset}>
              {busy === "reset" ? "Resetting…" : "Reset"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setConfirmReset(true)}>
            Reset test data
          </Button>
        )}
      </div>
    </div>
  );
}
